import {
  Button,
  Flex,
  Heading,
  IconAddLine,
  IconButton,
  IconEditLine,
  IconTrashLine,
  List,
  Pill,
  Text,
  ToggleDetails,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { AssessmentPlanService } from "../../services/AssessmentService";
import { CyclesService } from "../../services/CyclesService";
import { UserDetailContext } from "../../utils/Context";
import { Card } from "../Card";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../DialogYesNo";
import { CycleEditModal } from "./CycleEditModal";

export const CyclesAndGoals = () => {
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();
  const [componentDataIsInitialized, setComponentDataIsInitialized] =
    useState(false);

  const [trigger, setTrigger] = useState(true);
  const [cycles, setCycles] = useState([]);
  const [cycleEditModalOpen, setCycleEditModalOpen] = useState(false);
  const [editedCycle, setEditedCycle] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteData, setDeleteData] = useState({});

  const [usedCycles, setUsedCycles] = useState([]);

  useEffect(() => {
    if (trigger) {
      setTrigger(false);

      Promise.all([
        CyclesService.getCycles(),
        AssessmentPlanService.getLightAssessmentPlans(),
      ])
        .then(([cycles, assessments]) => {
          setCycles([
            ...cycles.sort(
              (a, b) => new Date(a.end_date) - new Date(b.end_date)
            ),
          ]);

          let tmp = assessments.map((x) => x.cycle.id);
          setUsedCycles(tmp);

          setComponentDataIsInitialized(true);
        })
        .catch((error) => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("cyclesAndGoals.cycles"),
            }),
            ERROR
          );
          console.error("Error loading cycles or assessments:", error);
        });
    }
  }, [trigger, userDetails]);

  const handleCycleButtonClick = () => {
    if (!editedCycle) {
      setEditedCycle({
        name: "",
        type: {},
        goals: [],
        start_date: "",
        end_date: "",
        parent: {},
        children: [],
      });
    }
    setCycleEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setCycleEditModalOpen(false);
    setEditedCycle({
      name: "",
      type: {},
      goals: [],
      start_date: "",
      end_date: "",
      parent: {},
      children: [],
    });
    setComponentDataIsInitialized(false);
    setTrigger(true);
  };

  const handleDeleteCycle = (cycle) => {
    setDeleteData(cycle);
    setOpenDialog(true);
  };

  const deleteCycle = async (cycleToBeDeleted) => {
    if (cycleToBeDeleted.id) {
      await CyclesService.deleteCycle(cycleToBeDeleted)
        .then(() => {
          setCycles([...cycles.filter((cycle) => cycle !== cycleToBeDeleted)]);
          setOpenDialog(false);
          showNotification(t("notifications.cycle.deleteCycleSuccess"));
        })
        .catch((e) => {
          showNotification(
            `${t("notifications.cycle.deleteCycleError")} ${e}`,
            ERROR
          );
        });
    } else {
      setCycles([...cycles.filter((cycle) => cycle !== cycleToBeDeleted)]);
      setOpenDialog(false);
    }
  };

  const handleEdit = (cycle) => {
    setEditedCycle(cycle);
    handleCycleButtonClick();
  };

  return (
    <>
      <div
        style={{
          padding: "0.125rem",
          marginBottom: "15px",
          borderBottom: "1px solid #ccc",
        }}
      >
        <Flex justifyItems="space-between" alignItems="end">
          <Flex.Item>
            <Heading as="h2" level="h3">
              {t("cyclesAndGoals.heading")}
            </Heading>
          </Flex.Item>
          <Flex.Item>
            <Button
              id="add-cycle-button"
              color="primary"
              renderIcon={IconAddLine}
              onClick={handleCycleButtonClick}
            >
              {t("cyclesAndGoals.addButton")}
            </Button>
          </Flex.Item>
        </Flex>
      </div>
      <div>
        {!componentDataIsInitialized ? (
          <Skeleton count={3} style={{ marginBottom: 10, height: 152 }} />
        ) : (
          <List isUnstyled>
            {cycles.map((cycle, key) => (
              <List.Item key={key}>
                <Card
                  style={{
                    backgroundColor: "#F2F8FA",
                    padding: "0",
                    height: "100%",
                  }}
                >
                  <Flex
                    justifyItems="space-between"
                    alignItems="start"
                    margin="0 0 small 0"
                    wrap="wrap"
                    padding="small"
                  >
                    <Flex.Item margin="0 0 small 0">
                      <Text weight="bold">{t("cyclesAndGoals.name")}: </Text>
                      {cycle.name}
                    </Flex.Item>
                    <Flex.Item margin="0 0 0 small">
                      <Pill>{`${new Date(
                        cycle.start_date
                      ).toLocaleDateString()} - ${new Date(
                        cycle.end_date
                      ).toLocaleDateString()}`}</Pill>
                    </Flex.Item>
                    <Flex.Item margin="0 0 0 small">
                      <IconButton
                        renderIcon={IconEditLine}
                        margin="0 small 0 0"
                        screenReaderLabel={t("cyclesAndGoals.editCycleButton")}
                        onClick={() => handleEdit(cycle)}
                      />
                      <Tooltip
                        renderTip={
                          usedCycles.includes(cycle.id)
                            ? t("cyclesAndGoals.cannotDeleteTooltip")
                            : t("cyclesAndGoals.deleteCycleButton")
                        }
                        placement="top"
                        on={["hover"]}
                      >
                        <IconButton
                          renderIcon={IconTrashLine}
                          screenReaderLabel={t(
                            "cyclesAndGoals.deleteCycleButton"
                          )}
                          interaction={
                            usedCycles.includes(cycle.id)
                              ? "disabled"
                              : "enabled"
                          }
                          onClick={() => handleDeleteCycle(cycle)}
                        />
                      </Tooltip>
                    </Flex.Item>
                    {cycle.type && (
                      <Flex.Item size="100%">
                        <Text weight="bold">{t("cyclesAndGoals.typeIs")} </Text>
                        {cycle.type.name},{" "}
                        {t("cyclesAndGoals.cycleType.durationEvery")}{" "}
                        {cycle.type.duration}
                      </Flex.Item>
                    )}
                    <Flex.Item size="100%" margin="small 0 0 0">
                      <Text weight="bold">
                        {t("cyclesAndGoals.cycleGoal.goalsAndObjectives")}
                      </Text>
                      {cycle.goals.map((goal, idx) => (
                        <ToggleDetails
                          key={idx}
                          size="small"
                          summary={
                            <Text as="div" size="small" weight="bold">
                              {t("assessment.goal")}:{" "}
                              <Text as="span" weight="normal" size="small">
                                {goal.description}
                              </Text>
                            </Text>
                          }
                        >
                          <Text as="div" size="small" weight="bold">
                            {t("assessment.objective")}:
                          </Text>
                          <List size="small">
                            {goal.objectives.map((obj, key) => (
                              <List.Item key={key}>{obj.description}</List.Item>
                            ))}
                          </List>
                        </ToggleDetails>
                      ))}
                    </Flex.Item>

                    <Flex.Item size="100%" margin="small 0 0 0">
                      <Flex justifyItems="start" alignItems="start" wrap="wrap">
                        {cycle.parent_cycle &&
                          Object.hasOwn(cycle.parent_cycle, "name") && (
                            <Flex.Item size="50%" padding="0 large 0 0">
                              <span
                                style={{
                                  fontSize: "0.9em",
                                  color: "#156380",
                                  fontWeight: "bold",
                                }}
                              >
                                {t("cyclesAndGoals.childOf")}
                              </span>
                              <List isUnstyled>
                                <List.Item>
                                  <span
                                    style={{
                                      fontSize: "0.9em",
                                      color: "#156380",
                                    }}
                                  >
                                    {cycle.parent_cycle.name}
                                  </span>
                                </List.Item>
                              </List>
                            </Flex.Item>
                          )}

                        {cycle.child_cycles.length > 0 && (
                          <Flex.Item>
                            <span
                              style={{
                                fontSize: "0.9em",
                                color: "#156380",
                                fontWeight: "bold",
                              }}
                            >
                              {t("cyclesAndGoals.parentOf")}
                            </span>
                            <List isUnstyled>
                              {cycle.child_cycles.map((child, idx) => (
                                <List.Item key={idx + 1}>
                                  <span
                                    style={{
                                      fontSize: "0.9em",
                                      color: "#156380",
                                    }}
                                  >
                                    {child.name}
                                  </span>
                                </List.Item>
                              ))}
                            </List>
                          </Flex.Item>
                        )}
                      </Flex>
                    </Flex.Item>
                  </Flex>
                </Card>
              </List.Item>
            ))}
          </List>
        )}
      </div>
      <DialogYesNo
        message={t("cyclesAndGoals.deleteCycleMessage")}
        openStatus={openDialog}
        onClickNo={() => setOpenDialog(false)}
        onClickYes={() => deleteCycle(deleteData)}
      />
      {cycleEditModalOpen && (
        <CycleEditModal
          open={cycleEditModalOpen}
          cycle={editedCycle}
          cycles={cycles}
          setCycles={setCycles}
          onCloseHandler={handleEditModalClose}
        />
      )}
    </>
  );
};
